import { computed, watch } from 'vue';
import { usePermissionStore } from '@/stores/usePermissionStore';
import { useAuthStore } from '@/stores/useAuthStore';

/**
 * 权限组合式函数
 * 提供权限检查的响应式API
 */
export function usePermission() {
  const permissionStore = usePermissionStore();
  const authStore = useAuthStore();

  /**
   * 检查是否拥有权限
   * @param permissionCodes 权限编码（单个或数组）
   * @param requireAll 是否要求所有权限（默认false，满足任意一个即可）
   */
  const hasPermission = computed(() => {
    return (permissionCodes: string | string[], requireAll = false): boolean => {
      if (!authStore.isAuthenticated) {
        return false;
      }
      return permissionStore.hasPermissions(permissionCodes, requireAll);
    };
  });

  /**
   * 检查是否拥有角色
   * @param roleCodes 角色编码（单个或数组）
   * @param requireAll 是否要求所有角色（默认false，满足任意一个即可）
   */
  const hasRole = computed(() => {
    return (roleCodes: string | string[], requireAll = false): boolean => {
      if (!authStore.isAuthenticated) {
        return false;
      }
      return permissionStore.hasRoles(roleCodes, requireAll);
    };
  });

  /**
   * 检查单个权限（响应式）
   */
  const checkPermission = (permissionCode: string): boolean => {
    if (!authStore.isAuthenticated) {
      return false;
    }
    return permissionStore.checkPermission(permissionCode);
  };

  /**
   * 检查单个角色（响应式）
   */
  const checkRole = (roleCode: string): boolean => {
    if (!authStore.isAuthenticated) {
      return false;
    }
    return permissionStore.checkRole(roleCode);
  };

  const checkMatrixPermission = (leafCode: string): boolean => {
    if (!authStore.isAuthenticated) {
      return false;
    }
    return permissionStore.checkMatrixPermission(leafCode);
  };

  /**
   * 刷新权限
   */
  const refreshPermissions = async (): Promise<void> => {
    await permissionStore.refreshPermissions();
  };

  /**
   * 监听认证状态变化，自动获取权限
   */
  watch(
    () => authStore.isAuthenticated,
    async (isAuthenticated) => {
      if (isAuthenticated) {
        await permissionStore.fetchPermissions();
      } else {
        permissionStore.clearCache();
      }
    },
    { immediate: true }
  );

  return {
    hasPermission,
    hasRole,
    checkPermission,
    checkMatrixPermission,
    checkRole,
    refreshPermissions,
    permissions: computed(() => permissionStore.permissions),
    matrixLeaves: computed(() => permissionStore.matrixLeaves),
    roles: computed(() => permissionStore.roles),
    loading: computed(() => permissionStore.loading),
  };
}
